import { captureException } from "@sentry/core";
import { BuildingGenerationErrorType } from "building-types/errors/buildingGenerationError";
import type { Formula } from "building-types/options/formula";
import { isMatchExpression } from "building-types/options/matchExpressionTypes";
import type { OptionChoices } from "building-types/options/optionChoices";
import type { OptionDefinitions } from "building-types/options/optionDefinitions";
import type { InstanceOptionSet } from "building-types/options/optionSet";
import type { RWTable, Table } from "common/data/table";
import { assertIsNotNullOrUndefined } from "common/utils/assertOfType";
import { DoubleWeakMap } from "common/utils/DoubleWeakMap";
import type { Mutable } from "common/utils/types";
import { getOptionSetArchetypeOrSelf } from "doc/options/getOptionSetArchetypeOrSelf";
import { optionSetDefaultOptionId } from "doc/options/optionSetBaseUtils";
import { optionSetSelectedOptionId } from "doc/options/optionSetTools";
import {
  evalFormula,
  formatFormulaEvalPath,
  type FormulaEvalPathSegment,
  type FormulaEvalContextInput,
} from "./formulaEval";
import { formulaGetIdentifiers, isFormula } from "./formulaParsing";
import { evalMatchExpression } from "./matchExpression";

export function evalFormulas(
  vInitial: any,
  env: (value: string, formula: Formula) => any = () => undefined,
  contextString?: string,
  entityDisabledByGroup?: Table<boolean>,
): any {
  const activeDisabledByGroup = getActiveDisabledByGroup(entityDisabledByGroup);

  if (!requiresFormulaEval(vInitial)) {
    if (!activeDisabledByGroup) {
      return vInitial;
    }
    return recursivelyDisableByGroup(vInitial, activeDisabledByGroup);
  }

  const pathStack: FormulaEvalPathSegment[] = [];
  if (contextString) {
    pathStack.push(contextString);
  }
  try {
    if (!activeDisabledByGroup) {
      return evalFormulasWithoutDisable(vInitial, env, pathStack);
    }
    return evalFormulasWithDisable(vInitial, env, pathStack, activeDisabledByGroup);
  } finally {
    if (contextString) {
      pathStack.pop();
    }
  }
}

function evalFormulasWithoutDisable(
  v: unknown,
  env: (value: string, formula: Formula) => any,
  pathStack: FormulaEvalPathSegment[],
): unknown {
  if (typeof v !== "object" || v === null) {
    return v;
  }

  if (ArrayBuffer.isView(v) || v instanceof RegExp || v instanceof Date) {
    return v;
  }

  if (v instanceof Array) {
    const length = v.length;
    let result: unknown[] | undefined;
    for (let i = 0; i < length; i++) {
      const child = v[i];
      const evaluated = evalFormulasWithoutDisableChild(child, env, pathStack, i);
      if (evaluated !== child) {
        result = copyArrayOnFirstMutation(result, v);
        result[i] = evaluated;
      }
    }
    return result ?? v;
  }

  if (isFormula(v)) {
    return evalFormula(v, pathStack, env, errorCallback);
  }

  if (isMatchExpression(v)) {
    const result = evalMatchExpression(v, env, pathStack, errorCallback);
    return result?.value;
  }

  let result: Record<string, unknown> | undefined;
  for (const key in v) {
    if (!Object.hasOwn(v, key)) {
      continue;
    }
    const child = (v as Record<string, unknown>)[key];
    const evaluated = evalFormulasWithoutDisableChild(child, env, pathStack, key);
    if (evaluated !== child) {
      result = copyRecordOnFirstMutation(result, v as Record<string, unknown>);
      result[key] = evaluated;
    }
  }
  return result ?? v;
}

function copyArrayOnFirstMutation(result: unknown[] | undefined, source: readonly unknown[]): unknown[] {
  if (result !== undefined) {
    return result;
  }
  // Clone once on the first changed index. Earlier indices were skipped because evaluated === child,
  // so source still holds the correct values for those slots.
  return source.slice();
}

function copyRecordOnFirstMutation(
  result: Record<string, unknown> | undefined,
  source: Record<string, unknown>,
): Record<string, unknown> {
  if (result !== undefined) {
    return result;
  }
  // Clone once on the first changed key. Keys processed earlier were skipped because evaluated === child.
  return { ...source };
}

function evalFormulasWithDisable(
  v: any,
  env: (value: string, formula: Formula) => any,
  pathStack: FormulaEvalPathSegment[],
  entityDisabledByGroup: Table<boolean>,
): any {
  if (typeof v !== "object" || v === null) {
    return v;
  }

  if (ArrayBuffer.isView(v) || v instanceof RegExp || v instanceof Date) {
    return v;
  }

  if (v instanceof Array) {
    const length = v.length;
    let result: unknown[] | undefined;
    for (let i = 0; i < length; i++) {
      const child = v[i];
      const evaluated = evalFormulasWithDisableChild(child, env, pathStack, entityDisabledByGroup, i);
      if (evaluated !== child) {
        result = copyArrayOnFirstMutation(result, v);
        result[i] = evaluated;
      }
    }
    return result ?? v;
  }

  if (isFormula(v)) {
    return evalFormula(v, pathStack, env, errorCallback);
  }

  if (isMatchExpression(v)) {
    const result = evalMatchExpression(v, env, pathStack, errorCallback);
    return result?.value;
  }

  const disableByGroup = isDisabledByGroup(entityDisabledByGroup, v);
  let result: Record<string, unknown> | undefined;

  for (const key in v) {
    if (!Object.hasOwn(v, key)) {
      continue;
    }

    if (disableByGroup && key === "disabled") {
      continue;
    }

    const child = v[key];
    const evaluated = evalFormulasWithDisableChild(child, env, pathStack, entityDisabledByGroup, key);
    if (evaluated !== child) {
      result = copyRecordOnFirstMutation(result, v as Record<string, unknown>);
      result[key] = evaluated;
    }
  }

  if (disableByGroup) {
    if (v.disabled === true && !result) {
      return v;
    }
    if (!result) {
      result = copyRecordOnFirstMutation(undefined, v as Record<string, unknown>);
    }
    assertIsNotNullOrUndefined(result);
    result.disabled = true;
    return result;
  }

  return result ?? v;
}

function evalFormulasWithDisableChild(
  child: unknown,
  env: (value: string, formula: Formula) => any,
  pathStack: FormulaEvalPathSegment[],
  entityDisabledByGroup: Table<boolean>,
  pathSegment: string | number,
): unknown {
  if (!requiresFormulaEval(child)) {
    return recursivelyDisableByGroup(child, entityDisabledByGroup);
  }

  pathStack.push(pathSegment);
  const evaluated = evalFormulasWithDisable(child, env, pathStack, entityDisabledByGroup);
  pathStack.pop();
  return evaluated;
}

function evalFormulasWithoutDisableChild(
  child: unknown,
  env: (value: string, formula: Formula) => any,
  pathStack: FormulaEvalPathSegment[],
  pathSegment: string | number,
): unknown {
  if (!requiresFormulaEval(child)) {
    return child;
  }

  pathStack.push(pathSegment);
  const evaluated = evalFormulasWithoutDisable(child, env, pathStack);
  pathStack.pop();
  return evaluated;
}

export function errorCallback(
  e: { type: BuildingGenerationErrorType; context: string },
  context: FormulaEvalContextInput,
) {
  if (e.type === BuildingGenerationErrorType.MISSING_EXPRESSION_OPTION_SET_ERROR) {
    e.context = typeof context === "string" ? context : formatFormulaEvalPath(context);
    throw e;
  }
}

const requiresFormulaEvalCache = new WeakMap<any, boolean>();

const instanceOptionSetsByArchetypeCache = new WeakMap<Table<InstanceOptionSet>, Table<Table<InstanceOptionSet>>>();

function getInstanceOptionSetsByArchetype(
  instanceOptionSets: Table<InstanceOptionSet>,
): Table<Table<InstanceOptionSet>> {
  const cached = instanceOptionSetsByArchetypeCache.get(instanceOptionSets);
  if (cached !== undefined) {
    return cached;
  }

  const archetypeInstanceOptionSets: Record<string, RWTable<InstanceOptionSet>> = {};
  for (const id in instanceOptionSets) {
    const row = instanceOptionSets[id];
    const { archetypeId } = row;
    if (!archetypeInstanceOptionSets[archetypeId]) {
      archetypeInstanceOptionSets[archetypeId] = {};
    }
    archetypeInstanceOptionSets[archetypeId][id] = row;
  }

  instanceOptionSetsByArchetypeCache.set(instanceOptionSets, archetypeInstanceOptionSets);
  return archetypeInstanceOptionSets;
}

function requiresFormulaEval(v: any): boolean {
  if (typeof v !== "object" || v === null) {
    return false;
  }

  const cached = requiresFormulaEvalCache.get(v);
  if (cached !== undefined) {
    return cached;
  }

  const res = requiresFormulaEvalInner(v);
  requiresFormulaEvalCache.set(v, res);
  return res;
}

function requiresFormulaEvalInner(v: object): boolean {
  if (ArrayBuffer.isView(v) || v instanceof RegExp || v instanceof Date) {
    return false;
  }

  if (v instanceof Array) {
    for (let i = 0, l = v.length; i < l; i++) {
      if (requiresFormulaEval(v[i])) {
        return true;
      }
    }
    return false;
  }

  if (isFormula(v)) {
    return true;
  }

  if (isMatchExpression(v)) {
    return true;
  }

  for (const key in v) {
    if (!Object.hasOwn(v, key)) {
      continue;
    }

    if (requiresFormulaEval(v[key])) {
      return true;
    }
  }

  return false;
}

const formulaIdentifierCache = new WeakMap<any, string[]>();

export function getFormulaIdentifiersInObject(v: any): string[] {
  if (typeof v !== "object" || v === null) {
    return [];
  }

  const cached = formulaIdentifierCache.get(v);
  if (cached !== undefined) {
    return cached;
  }

  const acc = {};
  getFormulaIdentifiersInObjectInner(v, acc);
  const keys = Object.freeze(Object.keys(acc)) as string[];
  formulaIdentifierCache.set(v, keys);
  return keys;
}

/** Derived instance option set ids contain ':' which expr-eval cannot tokenize as a single symbol. */
function formulaIdentifierOnParseFailure(formula: string): string | null {
  const trimmed = formula.trim();
  if (/^[A-Za-z_][A-Za-z0-9_:]*$/.test(trimmed)) {
    return trimmed;
  }
  return null;
}

function getFormulaIdentifiersInObjectInner(v: any, acc: { [id: string]: boolean } = {}) {
  if (typeof v !== "object" || v === null || ArrayBuffer.isView(v) || v instanceof RegExp || v instanceof Date) {
    return undefined;
  }

  if (v instanceof Array) {
    for (let i = 0, l = v.length; i < l; i++) {
      getFormulaIdentifiersInObjectInner(v[i], acc);
    }
    return undefined;
  }

  if (isFormula(v)) {
    try {
      for (const id of formulaGetIdentifiers(v)) {
        acc[id] = true;
      }
    } catch (e) {
      const fallbackId = formulaIdentifierOnParseFailure(v.formula);
      if (fallbackId !== null) {
        acc[fallbackId] = true;
      } else {
        captureException(e);
      }
    }

    return undefined;
  }

  for (const key in v) {
    if (!Object.hasOwn(v, key)) {
      continue;
    }

    getFormulaIdentifiersInObjectInner(v[key], acc);
  }

  return acc;
}

interface ResolveOptionsInFormulaCacheEntry {
  optionSets: OptionDefinitions["optionSets"];
  instanceOptionSets: OptionDefinitions["instanceOptionSets"];
  result: OptionChoices;
}

const resolveOptionsInFormulaCache = new WeakMap<any, WeakMap<OptionChoices, ResolveOptionsInFormulaCacheEntry>>();

function resolveOptionsInFormulaInner(
  obj: any,
  definitions: OptionDefinitions,
  optionChoices: OptionChoices,
): OptionChoices {
  const { instanceOptionSets } = definitions;
  const optionSetIds = getFormulaIdentifiersInObject(obj);
  const finalOptions: Mutable<OptionChoices> = {};

  // Resolve every formula-referenced identifier, including unknown ids (e.g. param_*, FALSE).
  // optionSetSelectedOptionId returns null for those; skipping them would leave undefined in
  // evalFormulas env and change disable-formula results.
  for (const id of optionSetIds) {
    if (Object.hasOwn(instanceOptionSets, id)) {
      continue;
    }
    finalOptions[id] = optionSetSelectedOptionId(definitions, optionChoices, id);
  }

  const instanceOptionSetsByArchetype = getInstanceOptionSetsByArchetype(instanceOptionSets);
  for (const archetypeId of Object.keys(finalOptions)) {
    const instances = instanceOptionSetsByArchetype[archetypeId];
    if (!instances) {
      continue;
    }
    for (const instanceId in instances) {
      if (Object.hasOwn(optionChoices, instanceId)) {
        continue;
      }
      finalOptions[instanceId] = optionSetSelectedOptionId(definitions, optionChoices, instanceId);
    }
  }

  for (const id of optionSetIds) {
    if (!Object.hasOwn(instanceOptionSets, id) || Object.hasOwn(finalOptions, id)) {
      continue;
    }
    finalOptions[id] = optionSetSelectedOptionId(definitions, optionChoices, id);
  }

  // Explicit instance choices (incl. null) must be present even when the archetype is not
  // formula-referenced on this object — evaluateEntityForComponentInstance checks Object.hasOwn.
  applyExplicitInstanceOptionChoices(finalOptions, instanceOptionSets, optionChoices);

  return finalOptions;
}

function applyExplicitInstanceOptionChoices(
  finalOptions: Mutable<OptionChoices>,
  instanceOptionSets: OptionDefinitions["instanceOptionSets"],
  optionChoices: OptionChoices,
): void {
  for (const instanceId in optionChoices) {
    if (!Object.hasOwn(instanceOptionSets, instanceId)) {
      continue;
    }
    finalOptions[instanceId] = optionChoices[instanceId];
  }
}

/**
 * Recursively finds all of the options referenced inside an object
 */
export function resolveOptionsInFormula(
  obj: any,
  definitions: OptionDefinitions,
  optionChoices: OptionChoices,
): OptionChoices {
  if (typeof obj === "object" && obj !== null) {
    const choicesCache = resolveOptionsInFormulaCache.get(obj);
    if (choicesCache) {
      const entry = choicesCache.get(optionChoices);
      if (
        entry &&
        entry.optionSets === definitions.optionSets &&
        entry.instanceOptionSets === definitions.instanceOptionSets
      ) {
        return entry.result;
      }
    }
  }

  const result = resolveOptionsInFormulaInner(obj, definitions, optionChoices);

  if (typeof obj === "object" && obj !== null) {
    let choicesCache = resolveOptionsInFormulaCache.get(obj);
    if (!choicesCache) {
      choicesCache = new WeakMap();
      resolveOptionsInFormulaCache.set(obj, choicesCache);
    }
    choicesCache.set(optionChoices, {
      optionSets: definitions.optionSets,
      instanceOptionSets: definitions.instanceOptionSets,
      result,
    });
  }

  return result;
}

export function resolveOptions(definitions: OptionDefinitions, optionChoices: OptionChoices): OptionChoices {
  const { optionSets, instanceOptionSets } = definitions;
  const finalOptions: Mutable<OptionChoices> = {};
  for (const id in optionSets) {
    finalOptions[id] = optionSetSelectedOptionId(definitions, optionChoices, id);
  }
  for (const instanceId in instanceOptionSets) {
    const instanceOptionSet = instanceOptionSets[instanceId];
    if (!Object.hasOwn(finalOptions, instanceOptionSet.archetypeId) && !Object.hasOwn(optionChoices, instanceId)) {
      continue;
    }
    finalOptions[instanceId] = Object.hasOwn(optionChoices, instanceId)
      ? optionChoices[instanceId]
      : optionSetSelectedOptionId(definitions, optionChoices, instanceId);
  }
  return finalOptions;
}

/**
 * recursivelyDisableByGroup can be very expensive to run, so we cache the results.
 */
const disabledByGroupCache = new DoubleWeakMap<any, Table<boolean>, any>();

export function recursivelyDisableByGroup(v: any, entityDisabledByGroup: Table<boolean> | undefined): any {
  if (!entityDisabledByGroup) {
    return v;
  }

  if (typeof v !== "object" || v === null || ArrayBuffer.isView(v) || v instanceof RegExp || v instanceof Date) {
    return v;
  }

  const cached = disabledByGroupCache.get(v, entityDisabledByGroup);
  if (cached !== undefined) {
    return cached;
  }

  const result = recursivelyDisableByGroupInner(v, entityDisabledByGroup);
  return disabledByGroupCache.set(v, entityDisabledByGroup, result);
}

function recursivelyDisableByGroupInner(v: object, entityDisabledByGroup: Table<boolean>): any {
  if (v instanceof Array) {
    const length = v.length;
    let result: unknown[] | undefined;
    for (let i = 0; i < length; i++) {
      const child = v[i];
      const disabled = recursivelyDisableByGroup(child, entityDisabledByGroup);
      if (disabled !== child) {
        if (!result) {
          result = v.slice();
        }
        assertIsNotNullOrUndefined(result);
        result[i] = disabled;
      }
    }
    return result ?? v;
  }

  if (isFormula(v)) {
    return v;
  }

  const record = v as Record<string, unknown>;
  const disableByGroup = isDisabledByGroup(entityDisabledByGroup, v);
  let result: Record<string, unknown> | undefined;

  for (const key in record) {
    if (!Object.hasOwn(record, key)) {
      continue;
    }

    if (disableByGroup && key === "disabled") {
      continue;
    }

    const child = record[key];
    const disabled = recursivelyDisableByGroup(child, entityDisabledByGroup);
    if (disabled !== child) {
      if (!result) {
        result = { ...record };
      }
      assertIsNotNullOrUndefined(result);
      result[key] = disabled;
    }
  }

  if (disableByGroup) {
    if (record.disabled === true && !result) {
      return v;
    }
    if (!result) {
      result = { ...record };
    }
    assertIsNotNullOrUndefined(result);
    result.disabled = true;
    return result;
  }

  return result ?? v;
}

function isDisabledByGroup(entityDisabledByGroup: Table<boolean>, v: object) {
  if (Object.hasOwn(v, "id")) {
    if (entityDisabledByGroup[(v as { id: string }).id]) {
      return true;
    }
  }
  return false;
}

const activeDisabledByGroupCache = new WeakMap<Table<boolean>, Table<boolean>>();
const inactiveDisabledByGroupCache = new WeakSet<Table<boolean>>();

function getActiveDisabledByGroup(entityDisabledByGroup: Table<boolean> | undefined): Table<boolean> | undefined {
  if (!entityDisabledByGroup) {
    return undefined;
  }

  const cachedActive = activeDisabledByGroupCache.get(entityDisabledByGroup);
  if (cachedActive !== undefined) {
    return cachedActive;
  }

  if (inactiveDisabledByGroupCache.has(entityDisabledByGroup)) {
    return undefined;
  }

  if (hasActiveDisabledByGroup(entityDisabledByGroup)) {
    activeDisabledByGroupCache.set(entityDisabledByGroup, entityDisabledByGroup);
    return entityDisabledByGroup;
  }

  inactiveDisabledByGroupCache.add(entityDisabledByGroup);
  return undefined;
}

function hasActiveDisabledByGroup(entityDisabledByGroup: Table<boolean>): boolean {
  for (const id in entityDisabledByGroup) {
    if (entityDisabledByGroup[id]) {
      return true;
    }
  }
  return false;
}

/**
 * Evaluates formulas on `componentEntity` with option-set variables resolved for a component instance
 * context. Formulas reference archetype option set ids; `componentInstanceChain` is the outer → inner path
 * to the placement being evaluated. Instance rows use derived keys in `optionChoices`
 * (`archetypeId:componentInstanceId`).
 */
export function evaluateEntityForComponentInstance<T>(
  componentEntity: T,
  componentInstanceChain: readonly string[],
  definitions: OptionDefinitions,
  optionChoices: OptionChoices,
): T {
  const { instanceOptionSets } = definitions;
  const instanceOptionSetsByArchetype = getInstanceOptionSetsByArchetype(instanceOptionSets);
  return evalFormulas(componentEntity, (archetypeId) => {
    if (componentInstanceChain.length === 0) {
      // 1. Empty chain — not instance-scoped
      return optionSetSelectedOptionId(definitions, optionChoices, archetypeId);
    }

    const archetypeInstances = instanceOptionSetsByArchetype[archetypeId];
    if (archetypeInstances) {
      // 2. First explicit choice on the chain (outer → inner); hasOwn on derived key only.
      // A missing key keeps scanning — not treated as unset.
      for (let i = 0; i < componentInstanceChain.length; i++) {
        const chainComponentInstanceId = componentInstanceChain[i];
        for (const instanceOptionSetKey in archetypeInstances) {
          const row = archetypeInstances[instanceOptionSetKey];
          if (row.componentInstanceId !== chainComponentInstanceId) {
            continue;
          }
          if (Object.hasOwn(optionChoices, instanceOptionSetKey)) {
            return optionChoices[instanceOptionSetKey];
          }
        }
      }
    }

    // 3. Innermost chain id has an instanceOptionSet row but no option choice so resolve to archetype default.
    // - only checks current entity -- ignore IOS rows on other component instances (higher on chain or outside the chain)
    const innermostComponentInstanceId = componentInstanceChain[componentInstanceChain.length - 1];
    const instanceOptionSetExistsForComponentInstance =
      archetypeInstances !== undefined &&
      Object.values(archetypeInstances).some((row) => row.componentInstanceId === innermostComponentInstanceId);
    if (instanceOptionSetExistsForComponentInstance) {
      const optionSet = getOptionSetArchetypeOrSelf(definitions, archetypeId);
      if (!optionSet || !definitions.optionSets) {
        return null;
      }
      return optionSetDefaultOptionId(optionSet, definitions.optionSets);
    }

    // 4. No instance row on innermost chain id — resolve optionChoices[archetypeId] if set, else default.
    return optionSetSelectedOptionId(definitions, optionChoices, archetypeId);
  });
}
